import { Button, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { IconFolder } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { buildFolderPath } from 'src/data-capture/file-management/utils/buildFolderPath';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import PageLoader from 'src/UI/loader/PageLoader';
import { SaveOwner } from './PresentedFileCard';

type Props = {
  opened: boolean;
  onClose: () => void;
  owner: SaveOwner;
  onSave: (parentId: string | null) => Promise<void>;
  isSaving: boolean;
};

const ROOT_OPTION = { value: '', label: 'Documents' };

const ownerId = (file: UploadedFile) =>
  file.owner?.kind === 'client' ? file.owner.clientId : file.owner?.familyId;

export function SaveToDocumentsModal({ opened, onClose, owner, onSave, isSaving }: Props) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const title = owner.kind === 'client' ? 'Save to client documents' : 'Save to family documents';

  // Queried directly rather than via useFileUploads: that hook expects a
  // CurrentClientProvider, which the sidebar (rendered on every page) is not always inside.
  const {
    data: files = [],
    isLoading,
    isError,
  } = useQuery<UploadedFile[]>({
    queryKey:
      owner.kind === 'client'
        ? ['clients', owner.id, 'data-upload-files']
        : ['families', owner.id, 'data-upload-files'],
    enabled: opened,
    staleTime: 30000,
  });

  // A family's document list also carries its members' files, so the folders have to be
  // narrowed to the ones this save would actually file into. Offering a member's folder for
  // a family-owned document saves it somewhere the family view cannot show it.
  const ownFiles = files.filter(file => ownerId(file) === owner.id);
  const folderOptions = [
    ROOT_OPTION,
    ...ownFiles
      .filter(file => file.isFolder)
      .map(folder => {
        const path = buildFolderPath(folder, ownFiles);
        return {
          value: folder.id,
          label: path ? `${path} / ${folder.file.fileName}` : folder.file.fileName,
        };
      }),
  ];

  const handleSave = async () => {
    try {
      await onSave(selectedFolderId || null);
      onClose();
    } catch {
      // The mutation hook has already shown the error notification; staying open lets
      // the user retry (or pick another folder) without reopening the modal.
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md">
      {isLoading && <PageLoader absolute={false} />}
      {isError && (
        <Text size="textSm" c="red.7">
          The folders could not be loaded. Please try again.
        </Text>
      )}
      {!isLoading && !isError && (
        <Stack gap="md">
          <Select
            label="Choose a folder"
            placeholder="Select folder"
            data={folderOptions}
            value={selectedFolderId ?? ''}
            onChange={setSelectedFolderId}
            leftSection={<IconFolder size={16} />}
            comboboxProps={{ withinPortal: true }}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={isSaving}>
              Save
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
